import React, { useEffect, useRef, useState } from 'react';

/**
 * Restrained entrance animation: a short fade + 14px rise, once, on scroll.
 * Uses IntersectionObserver directly so redesigned public pages do not pull
 * additional animation machinery into their render path.
 */
const Reveal = ({ as: Tag = 'div', delay = 0, className = '', style, children, ...rest }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );

    observer.observe(node);

    // Safety net: this is public marketing content, so it must never stay at
    // opacity 0 if the observer is throttled or never fires.
    const fallback = setTimeout(() => setVisible(true), 2000);

    return () => {
      clearTimeout(fallback);
      observer.disconnect();
    };
  }, []);

  return (
    <Tag
      ref={ref}
      className={`ax-reveal${visible ? ' is-visible' : ''}${className ? ` ${className}` : ''}`}
      style={{ transitionDelay: delay ? `${delay}ms` : undefined, ...style }}
      {...rest}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
